import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { HidableComponent } from '..';
import useEdgeDetectionContext from '../PluginsEditor/hooks/useEdgeContext';
import useRepositionToBeInView from '../util/useRepositionToBeInView';

export type Props = {
  dataTestId?: string;
  isVisible: boolean;
  onClickOutside: () => void;
  elementToPlaceUnder: HTMLElement | null;
};

const ResponsivePopup: React.FC<Props> = ({
  dataTestId,
  onClickOutside,
  isVisible,
  elementToPlaceUnder,
  children,
}) => {
  const edge = useEdgeDetectionContext();

  const [componentRef, transform, recalculatePosition] =
    useRepositionToBeInView<HTMLDivElement>({
      element: elementToPlaceUnder,
      placement: 'bottom-right',
    });

  useEffect(() => {
    if (elementToPlaceUnder !== null) {
      recalculatePosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, edge, elementToPlaceUnder]);

  if (isVisible) {
    return (
      <HidableComponent onClickOutside={onClickOutside} allowBubble>
        {ref => {
          const storeAndForwardRef = (r: null | HTMLDivElement) => {
            componentRef.current = r;
            ref.current = r;
          };

          return (
            <PositionContainer
              ref={storeAndForwardRef}
              translateX={transform.x}
              translateY={transform.y}
              data-testid={dataTestId}
            >
              {children}
            </PositionContainer>
          );
        }}
      </HidableComponent>
    );
  }

  return null;
};

const PositionContainer = styled.div<{
  translateX: number;
  translateY: number;
}>`
  position: absolute;
  top: 2.75em;

  ${({ theme, translateX, translateY }) => css`
    transform: translateX(${translateX}px) translateY(${translateY}px);
    z-index: ${theme.z('top')};
  `}
`;

// Wrap the children with this component if you want to style the element
export const BoxShadowContainer = styled.div<{}>`
  width: 200px;

  ${({ theme }) => css`
    border-radius: ${theme.getTokens().border.radius.base};
    background-color: ${theme.color('white')};
    padding: ${theme.space('s')};
    box-shadow: ${theme.boxShadow('base')};
  `}
`;

export default ResponsivePopup;
